import React, { useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import { useToast } from '../contexts/ToastContext';
import { adminHideMediaImage } from '../services/api/adminExpansion';

/** Admin tool to hide unsafe build media by image ID. */
export const AdminBuildMedia: React.FC = () => {
  const { showToast } = useToast();
  const [mediaImageId, setMediaImageId] = useState('');
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const requestHide = (e: React.FormEvent) => {
    e.preventDefault();
    const imageId = Number(mediaImageId);
    if (!Number.isInteger(imageId) || imageId <= 0) {
      showToast('Enter a valid image ID.', 'error');
      return;
    }
    setPendingId(imageId);
  };

  const confirmHide = async () => {
    if (pendingId == null) return;
    setBusy(true);
    try {
      const hidden = await adminHideMediaImage(pendingId);
      showToast(
        hidden ? `Image ${pendingId} hidden.` : 'No matching active image was found.',
        hidden ? 'success' : 'warning'
      );
      if (hidden) setMediaImageId('');
      setPendingId(null);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Hide failed', 'error');
      setPendingId(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader
        eyebrow="Admin"
        title="Build media"
        description="Hide unsafe uploaded images by ID. Files are not deleted; they stop rendering publicly."
      />

      <form onSubmit={requestHide} className="form-section space-y-3">
        <h2 className="text-white font-medium">Hide unsafe media</h2>
        <p className="text-gray-400 text-sm">
          Enter the vehicle image ID from reports or logs. The owner keeps the file but it stops rendering
          everywhere on the platform.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <label htmlFor="media-image-id" className="sr-only">
            Image ID
          </label>
          <input
            id="media-image-id"
            type="number"
            min={1}
            inputMode="numeric"
            value={mediaImageId}
            onChange={(e) => setMediaImageId(e.target.value)}
            placeholder="Image ID"
            className="input flex-1"
          />
          <Button type="submit" variant="danger">
            Hide image
          </Button>
        </div>
      </form>

      <p className="text-gray-500 text-sm">
        External video removal is not supported here. Remove unsafe external videos from the vehicle media
        manager instead.
      </p>

      <ConfirmDialog
        isOpen={pendingId != null}
        title="Hide media"
        description={
          pendingId != null
            ? `Hide image #${pendingId} everywhere on the platform? The owner keeps the file but it stops rendering publicly.`
            : ''
        }
        confirmLabel="Hide image"
        destructive
        loading={busy}
        loadingLabel="Working…"
        onConfirm={() => void confirmHide()}
        onCancel={() => setPendingId(null)}
      />
    </div>
  );
};

export default AdminBuildMedia;
